import { useQuery } from '@tanstack/react-query'
import React from 'react'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'

import { type TimeSeriesAnalysisPage_MainQuery as Data } from '~/gql/graphql'
import {
  ensureExists,
  exists,
  filterExists,
  findByPath,
  pathAssume,
  pathOr,
  SurveyOp,
} from '~/utils'

import { useMainQueryKey } from './useMainQueryKey'

export const useMainQueryData = () => {
  const { t } = useTranslation()
  const queryKey = useMainQueryKey()

  const { data } = useQuery<Data>({ enabled: false, queryKey })

  return React.useMemo(() => {
    const brandAccount = data?.brandAccount

    if (!exists(brandAccount)) {
      return null
    }

    const surveys = R.pipe(
      brandAccount.surveys,
      filterExists,
      R.map((survey) => ({
        ...survey,
        metadata: ensureExists(survey.metadata, t('assertion_failed')),
        customerAttributeGroups: R.pipe(
          survey.customerAttributeGroups,
          filterExists,
          R.map((customerAttributeGroup) => ({
            ...customerAttributeGroup,
            customerAttributes: R.pipe(
              customerAttributeGroup.customerAttributes,
              filterExists,
              R.map((customerAttribute, index) => ({
                ...customerAttribute,
                idLike: `${index + 1}`,
              })),
            ),
          })),
        ),
      })),
      R.sort(SurveyOp.compareByStartDate),
    )

    return R.pipe(brandAccount, (brandAccount) => ({
      ...brandAccount,
      surveyGroups: R.pipe(
        brandAccount.surveyGroups,
        filterExists,
        R.map((surveyGroup) => {
          const surveyGroupSurvey = R.pipe(surveyGroup.surveys, filterExists)

          return {
            ...surveyGroup,
            surveys: surveyGroupSurvey,
            brands: R.pipe(surveyGroup.brands, filterExists),
            questions: R.pipe(
              surveyGroup.questions,
              filterExists,
              R.map((question) => ({
                ...question,
                answerOptions: R.pipe(question.answerOptions, filterExists),
              })),
            ),
            customerAttributeGroups: R.pipe(
              surveyGroup.customerAttributeGroups,
              filterExists,
              R.filter(R.prop('showCustomizedKPI')),
              R.map((customerAttributeGroup) => {
                const pickedCustomerAttributeGroup = {
                  ...R.pick(customerAttributeGroup, ['id', 'name']),
                  attachedCustomerAttributeGroups: R.pipe(
                    customerAttributeGroup.attachedCustomerAttributeGroups ??
                      [],
                    filterExists,
                  ),
                }

                const customerAttributes = R.pipe(
                  customerAttributeGroup.customerAttributes,
                  filterExists,
                )

                if (
                  customerAttributes.every((customerAttribute) =>
                    exists(customerAttribute.id),
                  )
                ) {
                  return {
                    ...pickedCustomerAttributeGroup,
                    customerAttributes: customerAttributes.map(
                      (customerAttribute) => ({
                        idLike: ensureExists(
                          customerAttribute.id,
                          t('assertion_failed'),
                        ),
                        name: customerAttribute.name,
                        attachedCustomerAttributes: R.pipe(
                          customerAttribute.attachedCustomerAttributes,
                          filterExists,
                        ),
                      }),
                    ),
                  }
                }

                const customerAttributeGroups = surveyGroupSurvey.map(
                  (surveyGroupSurvey) => {
                    const customerAttributeGroupId = R.pipe(
                      customerAttributeGroup.attachedCustomerAttributeGroups ??
                        [],
                      findByPath('surveyId', surveyGroupSurvey.id),
                      pathAssume('customerAttributeGroupId'),
                    )

                    return R.pipe(
                      surveys,
                      findByPath('id', surveyGroupSurvey.id),
                      pathOr('customerAttributeGroups', []),
                      findByPath('id', customerAttributeGroupId),
                    )
                  },
                )

                const customerAttributeNames =
                  customerAttributeGroups[0]?.customerAttributes.map(
                    R.prop('name'),
                  ) ?? []
                const hasSameCustomerAttributeNames =
                  customerAttributeGroups.every(
                    (customerAttributeGroup) =>
                      exists(customerAttributeGroup) &&
                      R.isDeepEqual(
                        customerAttributeNames,
                        customerAttributeGroup.customerAttributes.map(
                          R.prop('name'),
                        ),
                      ),
                  )
                if (hasSameCustomerAttributeNames) {
                  return {
                    ...pickedCustomerAttributeGroup,
                    customerAttributes: customerAttributeNames.map(
                      (name, index) => ({
                        idLike: `${index + 1}`,
                        name,
                        attachedCustomerAttributes: [],
                      }),
                    ),
                  }
                }

                return {
                  ...pickedCustomerAttributeGroup,
                  customerAttributes: [],
                }
              }),
            ),
          }
        }),
      ),
      surveys,
    }))
  }, [data, t])
}

type BrandAccount = NonNullable<ReturnType<typeof useMainQueryData>>
export type SurveyGroup = BrandAccount['surveyGroups'][number]
export type SurveyGroupBrand = SurveyGroup['brands'][number]
export type SurveyGroupQuestion = SurveyGroup['questions'][number]
export type SurveyGroupCustomerAttributeGroup =
  SurveyGroup['customerAttributeGroups'][number]
export type Survey = BrandAccount['surveys'][number]
